<template>
  <div
    v-if="showLoader"
    class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
  >
    <svg
      class="animate-spin rounded-full h-16 w-16 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
        fill="none"
      />
    </svg>
  </div>

  <div v-else>
    <!-- FILTER BAR -->
    <div class="flex flex-wrap items-center justify-between pb-4 py-1">
      <div class="flex items-center space-x-4">
        <!-- Filter Dropdown + Inline Input + Clear -->
        <div class="relative flex items-center space-x-2">
          <!-- Dropdown Button -->
          <button
            type="button"
            @click="toggleDropdown"
            class="inline-flex items-center text-gray-400 bg-white border border-gray-300 rounded-md px-4 py-2 text-sm"
          >
            <svg class="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z" />
            </svg>
            {{ selectedFilter ? formatLabel(selectedFilter) : 'Select Filter' }}
            <svg class="w-2.5 h-2.5 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 4 4 4-4" />
            </svg>
          </button>

          <!-- Dropdown Menu -->
          <div v-if="isDropdownVisible" class="absolute top-full mt-1 left-0 z-10 bg-white border border-gray-300 rounded-md shadow-lg w-48">
            <ul class="py-1 text-sm text-gray-700">
              <li><button @click="applyFilter('fullName')" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Patient</button></li>
              <li><button @click="applyFilter('idNumber')" class="block w-full text-left px-4 py-2 hover:bg-gray-100">ID Number</button></li>
              <li><button @click="applyFilter('visitCode')" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Visit Code</button></li>
              <li><button @click="applyFilter('beneficiary')" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Beneficiary</button></li>
              <li><button @click="applyFilter('company')" class="block w-full text-left px-4 py-2 hover:bg-gray-100">Company</button></li>
            </ul>
          </div>

          <!-- Inline Input -->
          <input
            v-if="selectedFilter"
            v-model="filterValue"
            type="text"
            :placeholder="`Search ${formatLabel(selectedFilter).toLowerCase()}...`"
            class="w-64 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-custom-yellow"
          />

          <!-- Clear Button -->
          <button
            v-if="selectedFilter"
            @click="clearFilter"
            class="text-grey-500 text-sm font-medium rounded-md bg-red-500 px-3 py-2"
          >
            Clear
          </button>
        </div>

        <!-- DATE FILTERS -->
        <div class="flex items-center space-x-2 text-sm">
          <div class="flex items-center space-x-1">
            <label class="text-gray-600">From:</label>
            <input
              v-model="dateFrom"
              type="date"
              class="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            />
          </div>
          <div class="flex items-center space-x-1">
            <label class="text-gray-600">To:</label>
            <input
              v-model="dateTo"
              type="date"
              class="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-custom-yellow"
            />
          </div>
          <button
            v-if="dateFrom || dateTo"
            @click="clearDateFilter"
            class="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            Clear
          </button>
        </div>

        <button
          @click="exportToPDF"
          :disabled="exportLoading"
          class="flex items-center space-x-1 px-4 py-2 bg-custom-blue text-white text-sm rounded-md hover:opacity-90 disabled:opacity-50"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <span>{{ exportLoading ? 'Exporting...' : 'Export PDF' }}</span>
        </button>
      </div>
    </div>

    <!-- TABLE -->
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead class="bg-gray-100">
          <tr>
            <td
              v-for="(header, index) in tableHeaders"
              :key="index"
              class="px-6 py-3 border-b"
              scope="col"
            >
              <div class="flex items-center font-semibold text-base">
                {{ header }}
              </div>
            </td>
          </tr>
        </thead>
        <tbody v-if="coveredPatients.length > 0">
          <tr
            v-for="(patient, index) in coveredPatients"
            :key="index"
            class="border-b text-base"
          >
            <td class="px-6 py-2">
              {{ (currentPage - 1) * itemsPerPage + index + 1 }}
            </td>
            <td class="px-6 py-2">{{ patient.fullName }}</td>
            <td class="px-6 py-2">{{ patient.idNumber }}</td>
            <td class="px-6 py-2">{{ patient.visit_code }}</td>
            <td class="px-6 py-2">
              {{
                new Date(patient.visit_start_time).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })
              }}
            </td>
            <td class="px-6 py-2">{{ patient.beneficiary }}</td>
            <td class="px-6 py-2">{{ patient.company }}</td>
            <td class="px-6 py-2">KES {{ patient.total_amount }}</td>
            <td class="px-6 py-2">KES {{ patient.amount_paid }}</td>
            <td class="px-6 py-2">KES {{ patient.balance }}</td>
            <td class="px-6 py-2">
              <div class="flex items-center space-x-3">
                <span
                  v-if="patient.balance === 0"
                  class="inline-flex items-center px-3 py-1 rounded-md text-base font-medium bg-green-200 text-green-800 cursor-help"
                  title="This FFS bill has been fully settled."
                >
                  Fully Settled
                </span>
                <button v-else type="button" @click="openUpdateAmountPaidModal(patient.patient_id)">
                  <Edit stroke="blue" fill="none" size="24"/>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td :colspan="tableHeaders.length" class="px-6 py-4 text-center text-gray-500">
              No covered patients found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />
  </div>

  <!-- MODAL -->
  <EditFFSCoveredPatientsModal
    :show="showEditModal"
    :patient="selectedPatient"
    @close="closeEditModal"
    @updated="refreshAfterPay"
  />
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import apiClient from '@/services/api';
import { Edit } from 'lucide-vue-next';
import EditFFSCoveredPatientsModal from '@/views/modals/ffs-management/EditFFSCoveredPatientsModal.vue';
import { debounce } from 'lodash-es'; // npm install lodash-es
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PaginationComponent from '@/components/shared/pagination.vue';

// === STATE ===
const exportLoading = ref(false);
const showLoader = ref(false);
const coveredPatients = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(null);
const totalItems = ref(null);
const showEditModal = ref(false);
const selectedPatient = ref(null);

// Text filter states
const isDropdownVisible = ref(false);
const selectedFilter = ref(null);
const filterValue = ref('');

// Date filter states
const dateFrom = ref('');
const dateTo = ref('');

// === UI HELPERS ===
const toggleDropdown = () => {
  isDropdownVisible.value = !isDropdownVisible.value;
};

const applyFilter = (filter) => {
  selectedFilter.value = filter;
  isDropdownVisible.value = false;
  filterValue.value = '';
};

const formatLabel = (key) => {
  return key.split(/(?=[A-Z])/).join(' ').replace(/^\w/, c => c.toUpperCase());
};

const clearFilter = () => {
  filterValue.value = '';
  selectedFilter.value = null;
  currentPage.value = 1;
  fetchFFSCoveredPatients();
};

const goToPage = (pageNumber) => {
  currentPage.value = Number(pageNumber);
  fetchFFSCoveredPatients();
};

const changePageSize = (pageSize) => {
  itemsPerPage.value = Number(pageSize);
  currentPage.value  = 1;
  fetchFFSCoveredPatients();
};

// === MODAL ===
const openUpdateAmountPaidModal = (patient_id) => {
  const patient = coveredPatients.value.find(p => p.patient_id === patient_id);
  selectedPatient.value = patient;
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  selectedPatient.value = null;
};

const refreshAfterPay = () => {
  fetchFFSCoveredPatients();
  closeEditModal();
};

// === TABLE ===
const tableHeaders = [
  '#',
  'Patient',
  'ID Number',
  'Visit Code',
  'Visit Date',
  'Beneficiary',
  'Company',
  'Total Spent',
  'Total Paid',
  'Balance',
  'Action'
];

const toast = useToast();

// === DEBOUNCED SEARCH (≥3 chars, 500ms) ===
const debouncedSearch = debounce(() => {
  if (filterValue.value.length >= 3) {
    currentPage.value = 1;
    fetchFFSCoveredPatients();
  } else if (filterValue.value.length === 0 && selectedFilter.value) {
    currentPage.value = 1;
    fetchFFSCoveredPatients();
  }
}, 1000);

// Debounced Date Search
const debouncedDateSearch = debounce(() => {
  currentPage.value = 1;
  fetchFFSCoveredPatients();
}, 3000);

// Watch text input changes
watch(filterValue, () => {
  debouncedSearch();
});

// Watch date input changes
watch([dateFrom, dateTo], () => {
  debouncedDateSearch();
}, { deep: true })

// Reset on filter type change
watch(selectedFilter, (newVal, oldVal) => {
  if (newVal !== oldVal && !newVal) {
    currentPage.value = 1;
    fetchFFSCoveredPatients();
  }
});

// Clear date filters
const clearDateFilter = () => {
  dateFrom.value = '';
  dateTo.value = '';
  currentPage.value = 1;
  fetchFFSCoveredPatients();
}

// === API ===
const fetchFFSCoveredPatients = async () => {
  try {
    showLoader.value = true;

    const payload = {
      page: currentPage.value,
      per_page: itemsPerPage.value
    };

    // Text filter
    if (selectedFilter.value && filterValue.value.trim()) {
      payload.filter = payload.filter || {};
      payload.filter[selectedFilter.value] = filterValue.value.trim();
    }

    // Date filters
    if (dateFrom.value) {
      payload.filter = payload.filter || {};
      payload.filter.from = dateFrom.value;
    }

    if (dateTo.value) {
      payload.filter = payload.filter || {};
      payload.filter.to = dateTo.value;
    }

    const response = await apiClient.fetchFFSCoveredPatients(payload);

    if (response.success) {
      coveredPatients.value = response.data;
      currentPage.value = response.meta.current_page;
      totalItems.value = response.meta.total_items;
      totalPages.value = response.meta.total_pages;
      itemsPerPage.value = response.meta.per_page;
    } else {
      toast.error(response.message || 'Failed to fetch covered patients.');
    }
  } catch (error) {
    toast.error('Failed to fetch covered patients.');
    console.error('error fetching covered patients', error);
  } finally {
    showLoader.value = false;
  }
};

const exportToPDF = async () => {
  try {
    exportLoading.value = true;

    // Fetch all records matching current filters (bypass pagination)
    const payload = { page: 1, per_page: totalItems.value || 10000 };

    if (selectedFilter.value && filterValue.value.trim()) {
      payload.filter = {};
      payload.filter[selectedFilter.value] = filterValue.value.trim();
    }
    if (dateFrom.value) {
      payload.filter = payload.filter || {};
      payload.filter.from = dateFrom.value;
    }
    if (dateTo.value) {
      payload.filter = payload.filter || {};
      payload.filter.to = dateTo.value;
    }

    const response = await apiClient.fetchFFSCoveredPatients(payload);
    if (!response.success) {
      toast.error('Failed to fetch data for export.');
      return;
    }

    const rows = response.data;
    const doc = new jsPDF({ orientation: 'landscape' });

    const title = (selectedFilter.value && filterValue.value.trim())
      ? `FFS Covered Patients — ${formatLabel(selectedFilter.value)}: "${filterValue.value.trim()}"`
      : 'FFS Covered Patients — All Records';

    doc.setFontSize(14);
    doc.text(title, 14, 15);
    doc.setFontSize(9);
    doc.text(`Exported: ${new Date().toLocaleDateString('en-KE')}`, 14, 22);

    autoTable(doc, {
      startY: 28,
      head: [['#','Patient', 'ID Number', 'Visit Code', 'Visit Date', 'Beneficiary', 'Company', 'Total Spent', 'Total Paid', 'Balance']],
      body: rows.map((p, i) => [
        i + 1,
        p.fullName,
        p.idNumber,
        p.visit_code,
        new Date(p.visit_start_time).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
        p.beneficiary,
        p.company,
        `KES ${p.total_amount}`,
        `KES ${p.amount_paid}`,
        `KES ${p.balance}`,
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [30, 64, 175] },
    });

    const filename = (selectedFilter.value && filterValue.value.trim())
      ? `ffs-covered-${selectedFilter.value}-${filterValue.value.trim()}.pdf`
      : 'ffs-covered-patients-all.pdf';

    doc.save(filename);
  } catch (error) {
    toast.error('Failed to generate PDF.');
    console.error('PDF export error:', error);
  } finally {
    exportLoading.value = false;
  }
};

// === LIFECYCLE ===
onMounted(() => {
  fetchFFSCoveredPatients();
});
</script>

<style scoped>
/* Optional: tighten spacing */
.focus\:ring-custom-yellow:focus {
  --tw-ring-color: #facc15; /* or your custom yellow */
}
</style>